<template>
  <section class="payment-info-section">
    <rate-modal :visible.sync="visibleRateModal" />
    <rate-history :visible.sync="visibleRateHistory" />

    <section-divider title="Payment">
      <template slot="actions">
        <a-button class="flex gap-2 align-items-center" @click="visibleRateHistory = true">
          <a-icon width="16px" height="16px" icon="material-symbols:history" />
          History
        </a-button>
        <a-button
          v-if="
            hasUncontrolledPermission('manage_associates', {
              id: associate.id
            })
          "
          class="flex gap-2 align-items-center"
          @click="visibleRateModal = true"
        >
          <a-icon width="16px" height="16px" icon="material-symbols:edit-square-outline" />
          Change rate
        </a-button>
      </template>
    </section-divider>

    <div class="payment-info-grid">
      <div class="payment-info-field">
        <span>Employment Type</span>
        <strong>{{ paymentInfo.type }}</strong>
      </div>
      <template v-if="paymentInfo.legal_entity">
        <div class="payment-info-field">
          <span>Subtype</span>
          <strong>{{ AGENCY_TYPES[paymentInfo.legal_entity.type] }}</strong>
        </div>
        <div class="payment-info-field">
          <span>Legal Entity</span>
          <strong class="flex gap-2 align-items-center">
            {{ paymentInfo.legal_entity.name }}
            <a-button size="small" icon="eye" @click="$emit('open')" />
          </strong>
        </div>
      </template>

      <div v-if="canManageExternalStaffLink" class="payment-info-field">
        <span>External Staff</span>
        <a-button size="small" icon="link" type="primary" @click="handleExternalLink">
          {{ associate.staff_associate ? 'Disconnect' : 'Connect' }}
        </a-button>
      </div>

      <div class="payment-info-field">
        <span>Employment Status</span>
        <strong>{{ getPaymentStatus(paymentInfo.status) }}</strong>
      </div>
      <div class="payment-info-field">
        <span>Method</span>
        <strong>{{ getPaymentMethod(paymentInfo.method) }}</strong>
      </div>
      <div class="payment-info-field">
        <span>Hourly Rate, $</span>
        <div class="payment-info-value">
          <strong>{{ formatRate(paymentInfo.hourly_rate) }}</strong>
          <a-tooltip
            v-if="hourlyRateMinimumWarning"
            :title="hourlyRateMinimumWarning"
            :trigger="['hover', 'focus']"
          >
            <span
              class="minimum-pay-warning"
              role="img"
              tabindex="0"
              :aria-label="hourlyRateMinimumWarning"
            >
              <a-icon icon="material-symbols:warning-rounded" width="16px" height="16px" />
            </span>
          </a-tooltip>
        </div>
      </div>
      <div v-if="paymentInfo.method === 'salary'" class="payment-info-field">
        <span>Salary for Pay Period, $</span>
        <div class="payment-info-value">
          <strong>{{ formatRate(paymentInfo.salary) }}</strong>
          <a-tooltip
            v-if="salaryMinimumWarning"
            :title="salaryMinimumWarning"
            :trigger="['hover', 'focus']"
          >
            <span
              class="minimum-pay-warning"
              role="img"
              tabindex="0"
              :aria-label="salaryMinimumWarning"
            >
              <a-icon icon="material-symbols:warning-rounded" width="16px" height="16px" />
            </span>
          </a-tooltip>
        </div>
      </div>
      <div class="payment-info-field">
        <span>Orientation Rate, $</span>
        <strong>{{ formatRate(paymentInfo.orientation_rate) }}</strong>
      </div>
      <div class="payment-info-field">
        <span>Training Rate, $</span>
        <strong>{{ formatRate(paymentInfo.training_rate) }}</strong>
      </div>
      <div class="payment-info-field">
        <span>Allow Instant Payment</span>
        <a-switch
          :checked="associate.allow_instant_payment"
          @change="item => handleSwitchChange(item, 'allow_instant_payment')"
        />
      </div>
      <div class="payment-info-field">
        <span>Auto Approve Invoice</span>
        <a-switch
          :checked="associate.payment_auto_approve"
          @change="item => handleSwitchChange(item, 'payment_auto_approve')"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { AGENCY_TYPES } from '@/store/modules/legal-entity/constants'
import { PAYMENT_METHOD, PAYMENT_WORK_STATUS } from '@/store/modules/payment/constants'
import { getMinimumPayWarnings } from '@/modules/profile/utils/minimum-pay'
import { Button, notification, Switch, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import RateModal from '../rate/rate-modal.vue'
import RateHistory from '../rate/rate-history.vue'
import permissionMixin from '@/mixins/permissionMixin'
import { deepCopy } from '@/utils/deep-copy'
import SectionDivider from '@/components/ui/section-divider.vue'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')

export default {
  components: {
    'a-button': Button,
    'a-switch': Switch,
    'a-tooltip': Tooltip,
    'rate-modal': RateModal,
    'rate-history': RateHistory,
    'section-divider': SectionDivider
  },
  mixins: [permissionMixin],
  data() {
    return {
      AGENCY_TYPES,

      visibleRateModal: false,
      visibleRateHistory: false
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    paymentInfo() {
      return this.associate?.payment_info ?? {}
    },

    canManageExternalStaffLink() {
      return this.associate?.staff_associate || this.paymentInfo.legal_entity?.staffing_status === 'linked'
    },

    minimumPayWarnings() {
      return getMinimumPayWarnings(
        this.paymentInfo,
        this.$store.state.applicationState.currentProject?.payment_settings
      )
    },

    hourlyRateMinimumWarning() {
      return this.minimumPayWarnings.hourly_rate
    },

    salaryMinimumWarning() {
      return this.minimumPayWarnings.salary
    }
  },
  methods: {
    ...profileActions(['updateAssociate', 'setAssociate']),
    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    handleExternalLink() {
      this.$emit(
        'externalLink',
        this.associate.staff_associate ? 'unlink_from_staff_associate' : 'link_to_staff_associate'
      )
    },
    getPaymentMethod(method) {
      return PAYMENT_METHOD[method]
    },
    getPaymentStatus(status) {
      return PAYMENT_WORK_STATUS[status]
    },
    formatRate(rate) {
      return typeof rate === 'number' ? rate / 100 : ' - '
    },
    async handleSwitchChange(value, field) {
      const payload = {
        associate: {
          ...this.associate,
          [field]: value
        }
      }

      const tempAssociate = deepCopy(this.associate)

      this.setAssociate(payload.associate)

      try {
        await this.updateAssociate(payload)
      } catch (error) {
        this.setAssociate(tempAssociate)

        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    }
  }
}
</script>

<style scoped>
.payment-info-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.payment-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
}

.payment-info-field {
  min-width: 0;
}

.payment-info-field > span {
  display: block;
  margin-bottom: 4px;
  color: var(--text-color-secondary);
  font-size: 12px;
}

.payment-info-field > strong {
  display: block;
  overflow-wrap: anywhere;
}

.payment-info-value {
  display: flex;
  align-items: center;
  gap: 6px;
}

.minimum-pay-warning {
  display: inline-flex;
  color: var(--error-color);
  cursor: help;
}

@media screen and (max-width: 1024px) {
  .payment-info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 640px) {
  .payment-info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
